import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../components/layouts/DefaultPageLayout";
import { Button } from "../components/ui/button";
import { 
  FeatherArrowLeft,
  FeatherPlus,
  FeatherEdit2,
  FeatherTrash2,
  FeatherX,
  FeatherSave,
  FeatherClock,
  FeatherTag,
  FeatherSearch,
  FeatherLayout,
  FeatherFile,
  FeatherPin,
  FeatherShare2
} from "../components/ui/icons";
import { IconButton } from "../components/ui/IconButton";
import { IconWithBackground } from "../components/ui/IconWithBackground";
import BunnyLoader from "../components/ui/BunnyLoader";
import NoteReplies from "../components/shared/NoteReplies";
import api from "../services/api";
import "./Scribble.css";

const Scribble = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState({ title: "", content: "", tags: "" });
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTag, setFilterTag] = useState("all");
  const [saving, setSaving] = useState(false);
  const [quickCaptureMode, setQuickCaptureMode] = useState(false);
  const [quickNoteContent, setQuickNoteContent] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid, list, compact
  const [sortBy, setSortBy] = useState("created"); // created, updated, title
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedNoteForSharing, setSelectedNoteForSharing] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Load notes on component mount
  useEffect(() => {
    loadNotes();
    loadCurrentUser();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + N for new note
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setShowNewNoteForm(true);
      }
      // Ctrl/Cmd + K for quick capture
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setQuickCaptureMode(true);
      }
      // Escape to close forms
      if (e.key === 'Escape') {
        if (quickCaptureMode) {
          setQuickCaptureMode(false);
          setQuickNoteContent("");
        } else if (showNewNoteForm || editingNote) {
          cancelEditing();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [quickCaptureMode, showNewNoteForm, editingNote]);

  const loadNotes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/scribble');
      const notes = response.data || [];
      
      setNotes(notes);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setCurrentUser(response.data.user);
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const saveNote = async (noteData) => {
    try {
      setSaving(true);
      
      if (editingNote) {
        // Update existing note
        await api.put(`/api/scribble/${editingNote.id}`, noteData);
        setNotes(prev => prev.map(note => 
          note.id === editingNote.id 
            ? { ...note, ...noteData, updatedAt: new Date().toISOString() }
            : note
        ));
        setEditingNote(null);
      } else {
        // Create new note
        const response = await api.post('/api/scribble', noteData);
        setNotes(prev => [response.data, ...prev]);
        setNewNote({ title: "", content: "", tags: "" });
        setShowNewNoteForm(false);
      }
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const saveQuickNote = async () => {
    if (!quickNoteContent.trim()) return;
    
    try {
      setSaving(true);
      const response = await api.post('/api/scribble', {
        content: quickNoteContent.trim(),
        title: null,
        tags: []
      });
      setNotes(prev => [response.data, ...prev]);
      setQuickNoteContent("");
      setQuickCaptureMode(false);
    } catch (err) {
      console.error('Error saving quick note:', err);
      setError('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await api.delete(`/api/scribble/${noteId}`);
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  };

  const togglePin = async (noteId) => {
    try {
      const response = await api.patch(`/api/scribble/${noteId}/pin`);
      setNotes(prev => prev.map(note => 
        note.id === noteId ? response.data : note
      ));
    } catch (err) {
      console.error('Error toggling pin status:', err);
      setError('Failed to toggle pin status');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/scribble/users');
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    }
  };

  const openShareModal = async (note) => {
    setSelectedNoteForSharing(note);
    // Pre-select already shared users
    const alreadySharedUserIds = note.shared_with_users?.map(user => user.id) || [];
    setSelectedUsers(alreadySharedUserIds);
    await loadUsers();
    setShowShareModal(true);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const shareNote = async () => {
    if (!selectedUsers.length || !selectedNoteForSharing) return;
    
    try {
      setSharing(true);
      await api.post(`/api/scribble/${selectedNoteForSharing.id}/share`, {
        targetUserIds: selectedUsers
      });
      setShowShareModal(false);
      setSelectedNoteForSharing(null);
      setSelectedUsers([]);
      // Reload notes to get updated sharing information
      loadNotes();
    } catch (err) {
      console.error('Error sharing note:', err);
      setError('Failed to share note');
    } finally {
      setSharing(false);
    }
  };

  const startEditing = (note) => {
    setEditingNote(note);
    setNewNote({
      title: note.title || "",
      content: note.content || "",
      tags: note.tags ? note.tags.join(', ') : ""
    });
  };

  const cancelEditing = () => {
    setEditingNote(null);
    setNewNote({ title: "", content: "", tags: "" });
    setShowNewNoteForm(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const noteData = {
      title: newNote.title.trim(),
      content: newNote.content.trim(),
      tags: newNote.tags.trim() ? newNote.tags.split(',').map(tag => tag.trim()).filter(Boolean) : []
    };

    if (!noteData.title && !noteData.content) {
      return; // Don't save empty notes
    }

    saveNote(noteData);
  };

  // Filter notes based on search term and tag filter
  const filteredNotes = notes.filter(note => {
    const matchesSearch = !searchTerm || 
      note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesTag = filterTag === "all" || 
      note.tags?.some(tag => tag.toLowerCase() === filterTag.toLowerCase());
    
    return matchesSearch && matchesTag;
  });

  // Get unique tags from all notes
  const allTags = [...new Set(notes.flatMap(note => note.tags || []))];

  // Sort notes based on selected criteria
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    switch (sortBy) {
      case "updated":
        return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      case "title":
        return (a.title || "").localeCompare(b.title || "");
      case "created":
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Note templates for quick creation
  const noteTemplates = [
    { name: "Task", content: "☐ Task: \n\nNotes: \n\nDue: " },
    { name: "Meeting", content: "📅 Meeting: \n\nAgenda: \n\nAction Items: \n\nNext Steps: " },
    { name: "Idea", content: "💡 Idea: \n\nDescription: \n\nBenefits: \n\nImplementation: " },
    { name: "Reminder", content: "⏰ Reminder: \n\nWhen: \n\nWhat: \n\nPriority: " }
  ];

  const applyTemplate = (template) => {
    setNewNote(prev => ({ ...prev, content: template.content }));
    setShowNewNoteForm(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Unknown date';
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date received:', dateString);
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (diffInHours < 48) {
      return 'Yesterday at ' + date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  if (error) {
    return (
      <DefaultPageLayout>
        <div className="scribble-container">
          <div className="error-container">
            <div className="error">{error}</div>
            <Button onClick={loadNotes}>Retry</Button>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="scribble-container">
        {/* Header */}
        <div className="scribble-header">
          <div className="header-left">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/dashboard")}
            >
              Back
            </Button>
            <h1 className="text-heading-2">Scribble Board</h1>
            <div className="keyboard-shortcuts">
              <span className="shortcut-hint">⌘N New Note</span>
              <span className="shortcut-hint">⌘K Quick Capture</span>
            </div>
          </div>
          <div className="header-actions">
            <Button
              variant="neutral-secondary"
              icon={<FeatherPlus />}
              onClick={() => setQuickCaptureMode(true)}
              disabled={showNewNoteForm || editingNote}
              tooltip="Quick Capture (⌘K)"
            >
              Quick Note
            </Button>
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={() => setShowNewNoteForm(true)}
              disabled={showNewNoteForm || editingNote}
              tooltip="New Note (⌘N)"
            >
              New Note
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="search-filter-section">
          <div className="search-container">
            <div className="search-input-wrapper">
              <FeatherSearch size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          
          <div className="filter-controls">
            <div className="filter-container">
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="tag-filter"
              >
                <option value="all">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
            
            <div className="sort-container">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="created">Newest First</option>
                <option value="updated">Recently Updated</option>
                <option value="title">Alphabetical</option>
              </select>
            </div>
            
            <div className="view-controls">
              <IconButton
                variant={viewMode === "grid" ? "brand-primary" : "neutral-tertiary"}
                icon={<FeatherLayout />}
                onClick={() => setViewMode("grid")}
                tooltip="Grid View"
              />
              <IconButton
                variant={viewMode === "list" ? "brand-primary" : "neutral-tertiary"}
                icon={<FeatherFile />}
                onClick={() => setViewMode("list")}
                tooltip="List View"
              />
            </div>
          </div>
        </div>

        {/* New Note Form / Edit Form */}
        {(showNewNoteForm || editingNote) && (
          <div className="note-form-container">
            <form onSubmit={handleSubmit} className="note-form">
              <div className="form-header">
                <h3>{editingNote ? "Edit Note" : "New Note"}</h3>
                <div className="form-actions">
                  <IconButton
                    variant="neutral-tertiary"
                    icon={<FeatherSave />}
                    type="submit"
                    disabled={saving || (!newNote.title.trim() && !newNote.content.trim())}
                    tooltip={editingNote ? "Save Changes" : "Save Note"}
                  />
                  <IconButton
                    variant="neutral-tertiary"
                    icon={<FeatherX />}
                    onClick={cancelEditing}
                    tooltip="Cancel"
                  />
                </div>
              </div>
              
              <div className="form-content">
                <input
                  type="text"
                  placeholder="Title (optional)"
                  value={newNote.title}
                  onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                  className="note-title-input"
                />
                
                <textarea
                  placeholder="Write your note here..."
                  value={newNote.content}
                  onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                  className="note-content-input"
                  rows={10}
                />
                
                <input
                  type="text"
                  placeholder="Tags (comma separated)"
                  value={newNote.tags}
                  onChange={(e) => setNewNote(prev => ({ ...prev, tags: e.target.value }))}
                  className="note-tags-input"
                />
              </div>
            </form>
          </div>
        )}

        {/* Templates Section */}
        {!showNewNoteForm && !editingNote && (
          <div className="templates-section">
            <h4>Quick Templates</h4>
            <div className="templates-grid">
              {noteTemplates.map(template => (
                <button
                  key={template.name}
                  className="template-card"
                  onClick={() => applyTemplate(template)}
                >
                  <span className="template-name">{template.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes Grid */}
        <div className="notes-section">
          {loading ? (
            <div className="loading-container">
              <BunnyLoader 
                size="large" 
                text="Loading your notes..."
                animation="bounce"
              />
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="empty-state">
              <IconWithBackground
                variant="neutral"
                icon={<FeatherEdit2 />}
              />
              <h3>No notes yet</h3>
              <p>Start scribbling your thoughts, tasks, and ideas!</p>
              <Button
                variant="brand-primary"
                icon={<FeatherPlus />}
                onClick={() => setShowNewNoteForm(true)}
              >
                Create Your First Note
              </Button>
            </div>
          ) : (
            <div className="notes-grid">
              {sortedNotes.map(note => (
                <div key={note.id} className="note-card">
                  <div className="note-header">
                        <div className="note-meta">
                          {note.pinned && (
                            <span className="pinned-indicator">
                              <FeatherPin size={12} />
                            </span>
                          )}
                          {note.is_shared && (
                            <span className="shared-indicator" title={`Shared with ${note.shared_with_users?.length || 0} user${(note.shared_with_users?.length || 0) !== 1 ? 's' : ''} on ${new Date(note.shared_at).toLocaleDateString()}`}>
                              📤
                            </span>
                          )}
                          <span className="note-date">
                            <FeatherClock size={14} />
                            {formatDate(note.created_at)}
                          </span>
                        </div>
                        <div className="note-actions">
                          <IconButton
                            variant={note.pinned ? "brand-primary" : "neutral-tertiary"}
                            icon={<FeatherPin />}
                            onClick={() => togglePin(note.id)}
                            tooltip={note.pinned ? "Unpin from Dashboard" : "Pin to Dashboard"}
                          />
                          <IconButton
                            variant={note.is_shared ? "brand-primary" : "neutral-tertiary"}
                            icon={<FeatherShare2 />}
                            onClick={() => openShareModal(note)}
                            tooltip={note.is_shared ? `Shared with ${note.shared_with_users?.length || 0} user${(note.shared_with_users?.length || 0) !== 1 ? 's' : ''}` : "Share with Users"}
                          />
                          <IconButton
                            variant="neutral-tertiary"
                            icon={<FeatherEdit2 />}
                            onClick={() => startEditing(note)}
                            tooltip="Edit Note"
                          />
                          <IconButton
                            variant="neutral-tertiary"
                            icon={<FeatherTrash2 />}
                            onClick={() => deleteNote(note.id)}
                            tooltip="Delete Note"
                          />
                        </div>
                      </div>
                      
                      <div className="note-content">
                        {note.title && (
                          <h3 className="note-title">{note.title}</h3>
                        )}
                        <p className="note-text">{note.content}</p>
                      </div>
                      
                      {note.tags && note.tags.length > 0 && (
                        <div className="note-tags">
                          {note.tags.map(tag => (
                            <span key={tag} className="tag">
                              <FeatherTag size={12} />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {note.is_shared && (
                        <div className="note-sharing-info">
                          <div className="sharing-indicator">
                            <span className="sharing-icon">📤</span>
                            <span className="sharing-text">
                              Shared with {note.shared_with_users?.length || 0} user{(note.shared_with_users?.length || 0) !== 1 ? 's' : ''} on {new Date(note.shared_at).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                          </div>
                          {note.shared_with_users && note.shared_with_users.length > 0 && (
                            <div className="shared-users-list">
                              <span className="shared-users-label">Shared with:</span>
                              <div className="shared-users-tags">
                                {note.shared_with_users.map(user => (
                                  <span key={user.id} className="shared-user-tag">
                                    {user.display_name || user.email}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Note Replies */}
                      {note.is_shared && (
                        <NoteReplies 
                          noteId={note.id}
                          isSharedWithMe={note.shared_with_users?.some(user => user.id === currentUser?.id) || false}
                          currentUserId={currentUser?.id}
                          noteOwnerId={note.user_id}
                        />
                      )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Capture Overlay */}
        {quickCaptureMode && (
          <div className="quick-capture-overlay">
            <div className="quick-capture-modal">
              <div className="quick-capture-header">
                <h3>Quick Capture</h3>
                <div className="quick-capture-actions">
                  <IconButton
                    variant="neutral-tertiary"
                    icon={<FeatherSave />}
                    onClick={saveQuickNote}
                    disabled={saving || !quickNoteContent.trim()}
                    tooltip="Save (Enter)"
                  />
                  <IconButton
                    variant="neutral-tertiary"
                    icon={<FeatherX />}
                    onClick={() => {
                      setQuickCaptureMode(false);
                      setQuickNoteContent("");
                    }}
                    tooltip="Cancel (Esc)"
                  />
                </div>
              </div>
              <textarea
                placeholder="Type your note here... (Press Enter to save, Esc to cancel)"
                value={quickNoteContent}
                onChange={(e) => setQuickNoteContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    saveQuickNote();
                  }
                }}
                className="quick-capture-textarea"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div className="share-modal-overlay">
            <div className="share-modal">
              <div className="share-modal-header">
                <h3>Share Note</h3>
                <IconButton
                  variant="neutral-tertiary"
                  icon={<FeatherX />}
                  onClick={() => {
                    setShowShareModal(false);
                    setSelectedNoteForSharing(null);
                    setSelectedUsers([]);
                  }}
                  tooltip="Close"
                />
              </div>
              <div className="share-modal-content">
                <div className="note-preview">
                  <h4>Note Preview:</h4>
                  <p>{selectedNoteForSharing?.title || 'No title'}</p>
                  <p>{selectedNoteForSharing?.content?.substring(0, 100)}...</p>
                </div>
                <div className="user-selection">
                  <label>Select Users to Share With:</label>
                  <div className="user-checkboxes">
                    {users.map(user => (
                      <label key={user.id} className="user-checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                        />
                        <span className="user-info">
                          {user.display_name ? `${user.display_name} (${user.email})` : user.email}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedUsers.length > 0 && (
                    <div className="selected-users-summary">
                      <strong>Selected: {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}</strong>
                      {selectedNoteForSharing?.shared_with_users && selectedNoteForSharing.shared_with_users.length > 0 && (
                        <div className="current-sharing-info">
                          <small>Currently shared with: {selectedNoteForSharing.shared_with_users.map(user => user.display_name || user.email).join(', ')}</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="share-modal-actions">
                  <Button
                    variant="neutral-tertiary"
                    onClick={() => {
                      setShowShareModal(false);
                      setSelectedNoteForSharing(null);
                      setSelectedUsers([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand-primary"
                    onClick={shareNote}
                    disabled={sharing || selectedUsers.length === 0}
                  >
                    {sharing ? 'Sharing...' : `Share with ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};

export default Scribble; 